<template>
  <div class="s3-file-upload">
    <div class="upload-area" @drop="handleDrop" @dragover.prevent @dragenter.prevent>
      <input 
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        :multiple="multiple"
        style="display: none"
      />
      
      <div v-if="!uploading && !uploadedFiles.length" class="upload-placeholder">
        <i class="fas fa-cloud-upload-alt"></i>
        <p v-if="multiple">Arraste arquivos aqui ou <button type="button" @click="selectFiles" class="btn-link">clique para selecionar</button></p>
        <p v-else>Arraste um arquivo Excel aqui ou <button type="button" @click="selectFiles" class="btn-link">clique para selecionar</button></p>
      </div>
      
      <div v-if="uploading" class="upload-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
        </div>
        <p v-if="uploadProgress < 100">Enviando arquivo... {{ uploadProgress }}%</p>
        <p v-else class="text-success">
          <i class="fas fa-check-circle me-2"></i>
          Upload concluído! Salvando no sistema...
        </p>
      </div>
      
      <div v-if="uploadedFiles.length" class="uploaded-files">
        <div v-for="file in uploadedFiles" :key="file.key" class="uploaded-file">
          <i class="fas fa-file"></i>
          <span>{{ file.name }}</span>
          <button type="button" @click="removeFile(file)" class="btn-remove">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      <div v-if="error" class="error-message">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'S3FileUpload',
  props: {
    accept: {
      type: String,
      default: '*/*'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    }
  },
  data() {
    return {
      uploading: false,
      uploadProgress: 0,
      uploadedFiles: [],
      error: null
    }
  },
  methods: {
    selectFiles() {
      this.$refs.fileInput.click();
    },
    
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },
    
    handleDrop(event) {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    
    async processFiles(files) {
      this.error = null;
      
      // Validar se são arquivos Excel
      const nonExcelFiles = files.filter(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        return !['xls', 'xlsx'].includes(extension);
      });
      if (nonExcelFiles.length > 0) {
        this.error = 'Apenas arquivos Excel (.xls, .xlsx) são permitidos';
        return;
      }
      
      // Validar tamanho dos arquivos
      const oversizedFiles = files.filter(file => file.size > this.maxSize);
      if (oversizedFiles.length > 0) {
        this.error = `Arquivo(s) muito grande(s). Tamanho máximo: ${this.formatFileSize(this.maxSize)}`;
        return;
      }
      
      // Se não é múltiplo, limpar arquivos anteriores
      if (!this.multiple) {
        this.uploadedFiles = [];
      }
      
      for (const file of files) {
        await this.uploadFile(file);
      }
    },
    
    async uploadFile(file) {
      this.uploading = true;
      this.uploadProgress = 0;
      
      try {
        // 1. Obter presigned URL do Rails
        const response = await fetch('/api/s3_uploads/presigned_url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Erro ao obter URL de upload:', errorData);
          throw new Error(errorData.error || 'Erro ao obter URL de upload');
        }
        
        const { presigned_url, public_url, key } = await response.json();
        console.log('Presigned URL obtida:', presigned_url);
        
        // 2. Upload direto para S3
        const uploadResponse = await this.uploadToS3(presigned_url, file);
        
        if (uploadResponse.ok) {
          // 3. Adicionar à lista de arquivos enviados
          this.uploadedFiles.push({
            key,
            name: file.name,
            url: public_url,
            size: file.size
          });
          
          // Emitir evento para componente pai
          this.$emit('file-uploaded', {
            key,
            name: file.name,
            url: public_url,
            size: file.size,
            type: file.type
          });
        } else {
          throw new Error('Erro ao fazer upload do arquivo');
        }
        
      } catch (error) {
        console.error('Erro no upload:', error);
        this.error = error.message || 'Erro ao fazer upload do arquivo';
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },
    
    uploadToS3(presignedUrl, file) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            this.uploadProgress = Math.round((event.loaded / event.total) * 100);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve({ ok: true });
          } else {
            console.error('Upload falhou:', xhr.status, xhr.responseText);
            reject(new Error(`Upload falhou: ${xhr.status} - ${xhr.responseText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Erro de rede durante o upload'));
        });
        
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
    
    removeFile(file) {
      const index = this.uploadedFiles.findIndex(f => f.key === file.key);
      if (index > -1) {
        this.uploadedFiles.splice(index, 1);
        this.$emit('file-removed', file);
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
}
</script>